'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCityStore } from '@/store/useCityStore';

/* -------------------------------------------------------------------------- */
/* MOCK DATA                                                                   */
/* -------------------------------------------------------------------------- */

const searchTabs = [
    { id: 'buy', label: 'Buy' },
    { id: 'rent', label: 'Rent/Lease' },
    { id: 'projects', label: 'Projects' },
    { id: 'plots', label: 'Plots' },
    { id: 'civil', label: 'Civil Services' },
];

// const cities = [
//     { id: 1, name: 'Chennai', slug: 'chennai' },
//     { id: 2, name: 'Bangalore', slug: 'bangalore' },
//     { id: 3, name: 'Hyderabad', slug: 'hyderabad' },
//     { id: 4, name: 'Mumbai', slug: 'mumbai' },
//     { id: 5, name: 'Delhi', slug: 'delhi' },
//     { id: 6, name: 'Pune', slug: 'pune' },
// ];

// const localities = [
//     'Anna Nagar',
//     'Velachery',
//     'Adyar',
//     'T Nagar',
//     'Tambaram',
//     'OMR',
//     'Porur',
//     'Guindy',
//     'Kodambakkam',
// ];

/* -------------------------------------------------------------------------- */

export default function HeroSection() {
    const router = useRouter();

    const cityRef = useRef(null);
    const localityRef = useRef(null);

    const [activeTab, setActiveTab] = useState('buy');

    /* ---------------- CITY STORE ---------------- */
    const {
        cities,
        localities,
        selectedCity,
        selectedLocalities,
        citySearch,
        localitySearch,
        isLoading,
        setCitySearch,
        setLocalitySearch,
        setSelectedCity,
        setSelectedLocalities,
        addLocality,
        removeLocality,
        removeLastLocality,
        fetchCities,
        fetchLocalities,
    } = useCityStore();

    const hasMounted = useRef(false);

    /* ---------------- CITY DROPDOWN STATE ---------------- */
    const [showCityList, setShowCityList] = useState(false);
    const [cityIndex, setCityIndex] = useState(-1);

    const isShowingSelectedCity = citySearch && cities.some(c => c.id === selectedCity?.id && c.name.toLowerCase() === (citySearch || '').toLowerCase());
    const filteredCities = isShowingSelectedCity
        ? cities
        : cities.filter(c => c.name.toLowerCase().includes((citySearch || '').toLowerCase()));

    /* ---------------- LOCALITY DROPDOWN STATE ---------------- */
    const [showLocalityList, setShowLocalityList] = useState(false);
    const [localityIndex, setLocalityIndex] = useState(-1);
    const localitySuggestions = localities.filter(l => {
        const matchesSearch = (l?.name || '').toLowerCase().includes((localitySearch || '').toLowerCase());
        const notSelected = !selectedLocalities.some(sl => sl.id === l.id);
        return matchesSearch && notSelected;
    });

    /* ---------------- CLICK OUTSIDE ---------------- */
    useEffect(() => {
        const close = (e) => {
            if (cityRef.current && !cityRef.current.contains(e.target)) {
                setShowCityList(false);
                setCityIndex(-1);
            }
            if (localityRef.current && !localityRef.current.contains(e.target)) {
                setShowLocalityList(false);
                setLocalityIndex(-1);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    /* ---------------- SCROLL HIGHLIGHTED ITEM INTO VIEW ---------------- */
    useEffect(() => {
        if (cityIndex >= 0) {
            const el = document.getElementById(`city-item-${cityIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [cityIndex]);

    useEffect(() => {
        if (localityIndex >= 0) {
            const el = document.getElementById(`locality-item-${localityIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [localityIndex]);

    /* ---------------- KEYBOARD HANDLERS ---------------- */
    const handleCityKeyDown = async (e) => {
        if (!showCityList) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                setShowCityList(true);
            }
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCityIndex((prev) => (prev < filteredCities.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCityIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (cityIndex >= 0 && cityIndex < filteredCities.length) {
                const selected = filteredCities[cityIndex];
                setCitySearch(selected.name);
                setSelectedCity(selected);
                setShowCityList(false);
                setCityIndex(-1);
                fetchLocalities(selected.id);
                setSelectedLocalities([]);
                setLocalitySearch('');
            }
        } else if (e.key === 'Escape') {
            setShowCityList(false);
            setCityIndex(-1);
        }
    };

    const handleLocalityKeyDown = (e) => {
        if (e.key === 'Backspace' && !localitySearch && selectedLocalities.length > 0) {
            removeLastLocality();
            return;
        }

        if (!showLocalityList) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                setShowLocalityList(true);
            }
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setLocalityIndex((prev) => (prev < localitySuggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setLocalityIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (localityIndex >= 0 && localityIndex < localitySuggestions.length) {
                const selectedLocality = localitySuggestions[localityIndex];
                addLocality(selectedLocality);
                setLocalitySearch('');
                setLocalityIndex(-1);
            }
        } else if (e.key === 'Escape') {
            setShowLocalityList(false);
            setLocalityIndex(-1);
        }
    };

    useEffect(() => {
        if (hasMounted.current) return;
        hasMounted.current = true;
        // Only fetch if the store hasn't been populated yet (e.g. by Navbar)
        if (cities.length === 0) {
            fetchCities(null, null);
        }
    }, []);

    /* ---------------- SEARCH ---------------- */
    const handleSearch = (e) => {
        if (e) e.preventDefault();
        const city = selectedCity?.slug || selectedCity?.name?.toLowerCase() || 'all';
        const basePath = activeTab === 'projects' ? '/projects' : `/${activeTab}`;
        const params = new URLSearchParams();

        if (selectedLocalities.length > 0) {
            const localitiesSlugs = selectedLocalities.map(l => l.slug || l.name).join(',');
            params.set('q', localitiesSlugs);
        } else if (localitySearch) {
            params.set('q', localitySearch);
        }

        if (selectedCity?.id) params.set('cityId', selectedCity.id);
        if (selectedLocalities.length > 0) params.set('localities', selectedLocalities.map(l => l.id).join('-'));
        router.push(`${basePath}/${city}?${params.toString()}`);
    };

    /* -------------------------------------------------------------------------- */

    return (
        <section className="hero-section pt-12 overflow-visible">
            <div className="container-custom py-12">
                <div className="max-w-3xl mx-auto text-center">

                    <h1 className="text-2xl lg:text-4xl font-bold text-white mb-4">
                        Find your dream home
                    </h1>

                    {/* TABS */}
                    <div className="hero-search-tabs flex overflow-x-auto lg:overflow-visible whitespace-nowrap scrollbar-hide">
                        {searchTabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`tab flex-shrink-0 ${activeTab === tab.id ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* FORM */}
                    <form className="hero-search-box mt-4" onSubmit={handleSearch}>
                        <div className="flex flex-col md:flex-row gap-3">

                            {/* ================= CITY ================= */}
                            <div className="relative md:w-[30%]" ref={cityRef}>
                                <div className="relative">
                                    {/* Icon */}
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        📍
                                    </span>

                                    <input
                                        className="w-full h-10 pl-10 pr-10 bg-white rounded-sm outline-none text-sm"
                                        placeholder="Search city"
                                        value={citySearch}
                                        onFocus={() => setShowCityList(true)}
                                        onChange={(e) => {
                                            setCitySearch(e.target.value);
                                            setShowCityList(true);
                                            setCityIndex(-1);
                                        }}
                                        onKeyDown={handleCityKeyDown}
                                    />
                                </div>
                                {/* {citySearch && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCitySearch('');
                                            setSearchData({ ...searchData, city: '', cityId: '' });
                                            setLocalities([]);
                                            setSelectedLocalities([]);
                                            setLocalitySearch('');
                                            setShowCityList(true);
                                            setTimeout(() => cityRef.current?.querySelector('input')?.focus(), 0);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                    >
                                        ✕
                                    </button>
                                )} */}

                                {/* Dropdown */}
                                {showCityList && (
                                    <ul className="absolute z-50 mt-1 w-full bg-white rounded-sm shadow-xl max-h-60 overflow-auto">
                                        {filteredCities.map((c, i) => (
                                            <li
                                                key={c.id}
                                                id={`city-item-${i}`}
                                                className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer transition ${cityIndex === i ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100'
                                                    }`}
                                                onMouseEnter={() => setCityIndex(i)}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setCitySearch(c.name);
                                                    setSelectedCity(c);
                                                    setShowCityList(false);
                                                    fetchLocalities(c.id);
                                                    setSelectedLocalities([]);
                                                    setLocalitySearch('');
                                                }}
                                            >
                                                <span className="text-gray-400">📍</span>
                                                {c.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* ================= LOCALITY ================= */}
                            <div className="relative flex-1" ref={localityRef}>
                                <div className="relative flex items-center min-h-[40px] pl-10 pr-2 py-1 bg-white rounded-sm">
                                    {/* Icon */}
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        🏠
                                    </span>

                                    <div className="flex flex-wrap items-center gap-2 flex-1">
                                        {selectedLocalities.map((l) => (
                                            <div key={l.id} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                                <span>{l.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLocality(l.id)}
                                                    className="hover:text-blue-900 focus:outline-none"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                        <input
                                            className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1"
                                            placeholder={selectedLocalities.length === 0 ? "Search locality" : ""}
                                            value={localitySearch}
                                            onFocus={() => setShowLocalityList(true)}
                                            onChange={(e) => {
                                                setLocalitySearch(e.target.value);
                                                setShowLocalityList(true);
                                                setLocalityIndex(-1);
                                            }}
                                            onKeyDown={handleLocalityKeyDown}
                                        />
                                    </div>
                                </div>

                                {/* Dropdown */}
                                {showLocalityList && localitySuggestions.length > 0 && (
                                    <ul className="absolute z-50 mt-1 w-full bg-white rounded-sm shadow-xl max-h-60 overflow-auto scrollbar-hide">
                                        {localitySuggestions.map((l, i) => (
                                            <li
                                                key={l.id || i}
                                                id={`locality-item-${i}`}
                                                className={`flex items-center gap-0 px-4 py-2 text-sm cursor-pointer transition ${localityIndex === i ? 'bg-blue-50 text-blue-700 font-medium' : 'bg-white hover:bg-gray-100'
                                                    }`}
                                                onMouseEnter={() => setLocalityIndex(i)}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    addLocality(l);
                                                    setLocalitySearch('');
                                                }}
                                            >
                                                <span className="text-gray-400">📍</span>
                                                {l.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Desktop Search Icon */}
                            <button className="hidden lg:flex bg-primary hover:bg-primary-600 text-white rounded px-2 py-2 shadow-sm transition-all transform hover:scale-105 active:scale-95 items-center justify-center">
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </button>

                            {/* Mobile Search Button */}
                            <button className="flex lg:hidden items-center justify-center gap-2 bg-black hover:bg-primary-600 text-white rounded px-4 py-2 shadow-sm transition-all w-full">
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                                <span>Search</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
